import React from "react";
import type { Editor as TinyMceEditor } from "tinymce";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { useQueryClient } from "@tanstack/react-query";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import usePostData from "@/hooks/usePostData";
import EditorIntro from "@/pages/parameters/EditorIntro";
import {
  ArrowRight,
  BellRing,
  CheckCircle2,
  FileCheck2,
  FilePenLine,
  FileText,
  FlaskConical,
  FolderEdit,
  KeyRound,
  Mail,
  Send,
  ShieldCheck,
  Share2,
  UserCheck,
  UserPlus,
  Users,
  Wallet,
  XCircle,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_ENDPOINT;
const EMAIL_TEMPLATES_CACHE_TTL_MS = 1000 * 60 * 60;
const CALLS_EMAIL_BROWSER_CACHE_KEY = "parameters-email-calls";
const CALL_EMAIL_TEMPLATES_BROWSER_CACHE_KEY = "parameters-call-email-templates";

type CallListItem = {
  id: number;
  name: string;
  status?: number;
  endAt?: string | null;
  dateFin?: string | null;
  projectType?: {
    id?: number | null;
    name?: string | null;
  } | null;
  program?: {
    id?: number | null;
    name?: string | null;
  } | null;
};

type EmailModelItem = {
  key: string;
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
};

type StoredTemplate = {
  subject?: string;
  content?: string;
  useVerdeoDefault?: boolean;
};

type ResolvedTemplate = {
  mailType: string;
  source: "verdeo_default" | "carrier_global" | "call";
  module: string;
  title: string;
  description: string;
  subject: string;
  content: string | null;
  templatePath: string;
  useVerdeoDefault: boolean;
};

type MailTemplateApiResponse = {
  success: boolean;
  scope: "carrier_global" | "call";
  callId?: number;
  storedTemplates: Record<string, StoredTemplate>;
  globalTemplates?: Record<string, StoredTemplate>;
  resolvedTemplates: Record<string, ResolvedTemplate>;
};

type StarterKey = "native" | "recommended" | "blank";

type StarterTemplate = {
  label: string;
  description: string;
  subject: string;
  content: string;
};

type StarterTemplatesResponse = {
  success: boolean;
  mailType: string;
  templates: Record<StarterKey, StarterTemplate>;
};

type EmailModelGroup = {
  key: string;
  title: string;
  items: EmailModelItem[];
};

const GLOBAL_EMAIL_GROUPS: EmailModelGroup[] = [
  {
    key: "call_candidate_global",
    title: "Candidat",
    items: [
      {
        key: "call_candidate_manual_add",
        title: "Ajout manuel de candidat",
        description: "Email envoyé au candidat lorsque le porteur de projet l’ajoute manuellement à un appel à projet.",
        icon: UserPlus,
      },
      {
        key: "call_candidate_invitation",
        title: "Invitation à candidater",
        description: "Email envoyé au candidat pour participer à un appel à projet.",
        icon: Mail,
      },
      {
        key: "call_application_validated",
        title: "Candidature validée",
        description: "Email confirmant la validation d'un candidat.",
        icon: UserCheck,
      },
      {
        key: "call_candidate_access_recovery",
        title: "Récupération d’accès",
        description: "Email envoyé lorsqu’un candidat demande un nouvel accès à son appel.",
        icon: KeyRound,
      },
      {
        key: "call_submission_confirmation",
        title: "Confirmation de soumission",
        description: "Confirmation envoyée après la soumission d’une candidature.",
        icon: Send,
      },
      {
        key: "call_project_validated",
        title: "Projet validé",
        description: "Email envoyé quand le projet du candidat est validé.",
        icon: CheckCircle2,
      },
      {
        key: "call_project_rejected",
        title: "Projet rejeté",
        description: "Email envoyé quand le projet du candidat est rejeté.",
        icon: XCircle,
      },
    ],
  },
  {
    key: "call_pp_global",
    title: "Porteur de projet",
    items: [
      {
        key: "call_submission_received",
        title: "Notification : candidature reçue",
        description: "Email envoyé au porteur de projet pour l’informer qu’un candidat a soumis un projet.",
        icon: FileText,
      },
      {
        key: "call_submission_requires_approval",
        title: "Action requise : candidature à valider",
        description: "Email envoyé au porteur de projet lorsqu’une candidature nécessite une validation ou un rejet manuel.",
        icon: FolderEdit,
      },
      {
        key: "call_submission_auto_validated",
        title: "Notification : candidature validée automatiquement",
        description: "Email envoyé au porteur de projet lorsqu’une candidature est validée automatiquement.",
        icon: CheckCircle2,
      },
    ],
  },
];

const CALL_SPECIFIC_GROUPS: EmailModelGroup[] = [
  {
    key: "call_candidate",
    title: "Candidat",
    items: [
      {
        key: "call_candidate_manual_add",
        title: "Ajout manuel d’un candidat",
        description: "Email envoyé au candidat lorsque le porteur de projet l’ajoute manuellement à cet appel à projet.",
        icon: UserPlus,
      },
      {
        key: "call_candidate_invitation",
        title: "Invitation à candidater",
        description: "Email envoyé au candidat pour participer à cet appel à projet.",
        icon: Mail,
      },
      {
        key: "call_application_validated",
        title: "Candidature validée",
        description: "Email confirmant la validation d'un candidat.",
        icon: UserCheck,
      },
      {
        key: "call_candidate_access_recovery",
        title: "Récupération d’accès",
        description: "Email envoyé lorsqu’un candidat demande un nouvel accès à cet appel.",
        icon: KeyRound,
      },
      {
        key: "call_submission_confirmation",
        title: "Confirmation de soumission",
        description: "Confirmation envoyée après la soumission d’une candidature.",
        icon: Send,
      },
      {
        key: "call_project_validated",
        title: "Projet validé",
        description: "Email envoyé quand le projet du candidat est validé.",
        icon: CheckCircle2,
      },
      {
        key: "call_project_rejected",
        title: "Projet rejeté",
        description: "Email envoyé quand la candidature est rejetée.",
        icon: XCircle,
      },
    ],
  },
  {
    key: "call_pp",
    title: "Porteur de projet",
    items: [
      {
        key: "call_submission_received",
        title: "Candidature reçue",
        description: "Email envoyé au Porteur de Projet lorsqu’un candidat soumet un projet.",
        icon: FileText,
      },
      {
        key: "call_submission_requires_approval",
        title: "Candidature à approuver",
        description: "Email envoyé au Porteur de Projet lorsqu’une candidature nécessite sa décision.",
        icon: FolderEdit,
      },
      {
        key: "call_submission_auto_validated",
        title: "Candidature validée automatiquement",
        description: "Email envoyé au Porteur de Projet lorsqu’une candidature est validée automatiquement.",
        icon: CheckCircle2,
      },
    ],
  },
];

const formatDeadline = (value?: string | null) => {
  if (!value) return "Aucune date définie";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Aucune date définie";

  return parsed.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getCallDisplayName = (call: CallListItem) =>
  call.name?.trim() || call.program?.name?.trim() || `Appel à projet #${call.id}`;

const SectionCard = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-3xl border border-[#dfe9d8] bg-white p-5 shadow-[0_8px_30px_rgba(29,82,20,0.05)] md:p-6">
    <div className="mb-5 flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-[#18351b]">{title}</h2>
      <p className="max-w-3xl text-sm text-[#5f6d5c]">{description}</p>
    </div>
    {children}
  </section>
);

const EmailCards = ({
  items,
  onConfigure,
  sourceByKey,
  sourceLabels,
  customizedSources,
}: {
  items: EmailModelItem[];
  onConfigure?: (item: EmailModelItem) => void;
  sourceByKey?: Record<string, "verdeo_default" | "carrier_global" | "call">;
  sourceLabels?: {
    inherited: string;
    customized: string;
  };
  customizedSources?: Array<"verdeo_default" | "carrier_global" | "call">;
}) => (
  <div className="max-h-[340px] overflow-y-auto pr-1">
    <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => {
        const Icon = item.icon;
        const configurable = item.key.startsWith("call_");
        const source = sourceByKey?.[item.key];
        const isCustomized = source
          ? (customizedSources ?? ["carrier_global", "call"]).includes(source)
          : false;

        return (
          <div
            key={item.key}
            className="flex h-full flex-col rounded-2xl border border-[#dfe9d8] bg-[#fbfdf9] p-4 transition-colors hover:border-[#c7d8bb] hover:bg-white"
          >
            <div className="mb-3 flex items-start gap-3">
              <div className="rounded-2xl bg-[#edf6e7] p-2.5 text-[#486a2f]">
                <Icon className="size-4.5" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-[#18351b]">{item.title}</h4>
                {source && (
                  <span
                    className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      isCustomized
                        ? "bg-[#edf6e7] text-[#486a2f]"
                        : "bg-[#eef1f4] text-[#64707d]"
                    }`}
                  >
                    {isCustomized
                      ? sourceLabels?.customized ?? "Personnalisé"
                      : sourceLabels?.inherited ?? "Verdeo"}
                  </span>
                )}
                <p className="mt-1 min-h-[60px] text-xs leading-5 text-[#6c796a]">
                  {item.description}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className="mt-auto w-full border-[#d6e4cb] text-[#2f4b2d] hover:border-[#486a2f] hover:bg-[#edf6e7] hover:text-[#2d5a23]"
              disabled={!configurable}
              onClick={() => configurable && onConfigure?.(item)}
            >
              {configurable ? "Configurer" : "Bientôt disponible"}
            </Button>
          </div>
        );
      })}
    </div>
  </div>
);

const EmailGroupAccordion = ({
  groups,
  valuePrefix,
  onConfigure,
  sourceByKey,
  sourceLabels,
  customizedSources,
}: {
  groups: EmailModelGroup[];
  valuePrefix: string;
  onConfigure?: (item: EmailModelItem) => void;
  sourceByKey?: Record<string, "verdeo_default" | "carrier_global" | "call">;
  sourceLabels?: {
    inherited: string;
    customized: string;
  };
  customizedSources?: Array<"verdeo_default" | "carrier_global" | "call">;
}) => (
  <Accordion type="multiple" className="space-y-3">
    {groups.map((group) => (
      <AccordionItem
        key={`${valuePrefix}-${group.key}`}
        value={`${valuePrefix}-${group.key}`}
        className="overflow-hidden rounded-2xl border border-[#e1eadb] bg-[#fbfdf9] px-4"
      >
        <AccordionTrigger className="py-4 text-left hover:no-underline">
          <div className="flex min-w-0 items-center gap-3">
            <span className="text-sm font-semibold text-[#18351b]">{group.title}</span>
            <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-medium text-[#6c796a]">
              {group.items.length} modèle{group.items.length > 1 ? "s" : ""}
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-4">
          <EmailCards
            items={group.items}
            onConfigure={onConfigure}
            sourceByKey={sourceByKey}
            sourceLabels={sourceLabels}
            customizedSources={customizedSources}
          />
        </AccordionContent>
      </AccordionItem>
    ))}
  </Accordion>
);

type EditorState = {
  scope: "carrier_global" | "call";
  mailType: string;
  title: string;
  callId?: number;
  callName?: string;
  subject: string;
  content: string;
  useVerdeoDefault: boolean;
  hasStoredTemplate: boolean;
  source?: "verdeo_default" | "carrier_global" | "call";
};
type MailVariable = {
  key: string;
  label: string;
  token: string;
  description: string;
  isLink?: boolean;
};

const CALL_MAIL_VARIABLES: MailVariable[] = [
  { key: "call_name", label: "Nom de l'appel", token: "{{ call_name }}", description: "Nom de l'appel à projets." },
  { key: "candidate_name", label: "Nom du candidat", token: "{{ candidate_name }}", description: "Nom complet du candidat." },
  { key: "candidate_email", label: "Email du candidat", token: "{{ candidate_email }}", description: "Adresse email du candidat." },
  { key: "carrier_name", label: "Nom du Porteur de Projet", token: "{{ carrier_name }}", description: "Nom du porteur de projet." },
  { key: "project_name", label: "Nom du projet", token: "{{ project_name }}", description: "Nom du projet ou de la candidature." },
  { key: "access_code", label: "Code d'accès", token: "{{ access_code }}", description: "Code d'accès du candidat à l'appel." },
  { key: "call_url", label: "Lien vers l'appel", token: "{{ call_url }}", description: "Lien vers l'appel à projets.", isLink: true },
  { key: "project_url", label: "Lien vers le projet", token: "{{ project_url }}", description: "Lien vers le projet ou la candidature.", isLink: true },
];

const CALL_CANDIDATE_ACCESS_VARIABLES: MailVariable[] = [
  { key: "call_name", label: "Nom de l'appel", token: "{{ call_name }}", description: "Nom de l'appel à projets." },
  { key: "candidate_name", label: "Nom du candidat", token: "{{ candidate_name }}", description: "Nom complet du candidat." },
  { key: "candidate_email", label: "Email du candidat", token: "{{ candidate_email }}", description: "Adresse email du candidat." },
  { key: "carrier_name", label: "Nom du Porteur de Projet", token: "{{ carrier_name }}", description: "Nom du porteur de projet." },
  { key: "access_code", label: "Code d'accès", token: "{{ access_code }}", description: "Code d'accès du candidat à l'appel." },
  { key: "call_url", label: "Lien vers l'appel", token: "{{ call_url }}", description: "Lien vers l'appel à projets.", isLink: true },
];

const CALL_APPLICATION_VALIDATED_VARIABLES: MailVariable[] = [
  { key: "call_name", label: "Nom de l'appel", token: "{{ call_name }}", description: "Nom de l'appel à projets." },
  { key: "candidate_name", label: "Nom du candidat", token: "{{ candidate_name }}", description: "Nom complet du candidat." },
  { key: "candidate_email", label: "Email du candidat", token: "{{ candidate_email }}", description: "Email de connexion du candidat." },
  { key: "carrier_name", label: "Nom du Porteur de Projet", token: "{{ carrier_name }}", description: "Nom du porteur de projet." },
  { key: "access_code", label: "Mot de passe temporaire", token: "{{ access_code }}", description: "Mot de passe temporaire transmis au candidat validé." },
  { key: "login_url", label: "Lien de connexion", token: "{{ login_url }}", description: "Lien vers la page de connexion.", isLink: true },
];

const CALL_SUBMISSION_CONFIRMATION_VARIABLES: MailVariable[] = [
  { key: "call_name", label: "Nom de l'appel", token: "{{ call_name }}", description: "Nom de l'appel à projets." },
  { key: "candidate_name", label: "Nom du candidat", token: "{{ candidate_name }}", description: "Nom complet du candidat." },
  { key: "candidate_email", label: "Email du candidat", token: "{{ candidate_email }}", description: "Adresse email du candidat." },
  { key: "project_name", label: "Nom du projet", token: "{{ project_name }}", description: "Nom du projet soumis." },
  { key: "project_type_name", label: "Type de projet", token: "{{ project_type_name }}", description: "Type du projet soumis." },
  { key: "project_url", label: "Lien vers le projet", token: "{{ project_url }}", description: "Lien vers le projet ou la candidature.", isLink: true },
];

const CALL_PROJECT_DECISION_VARIABLES: MailVariable[] = [
  { key: "call_name", label: "Nom de l'appel", token: "{{ call_name }}", description: "Nom de l'appel à projets." },
  { key: "candidate_name", label: "Nom du candidat", token: "{{ candidate_name }}", description: "Nom complet du candidat." },
  { key: "candidate_firstname", label: "Prénom du candidat", token: "{{ candidate_firstname }}", description: "Prénom du candidat." },
  { key: "candidate_email", label: "Email du candidat", token: "{{ candidate_email }}", description: "Adresse email du candidat." },
  { key: "carrier_name", label: "Nom du Porteur de Projet", token: "{{ carrier_name }}", description: "Nom du porteur de projet." },
  { key: "project_name", label: "Nom du projet", token: "{{ project_name }}", description: "Nom du projet validé ou rejeté." },
  { key: "project_type_name", label: "Type de projet", token: "{{ project_type_name }}", description: "Type du projet." },
  { key: "project_url", label: "Lien vers le projet", token: "{{ project_url }}", description: "Lien vers le projet ou la candidature.", isLink: true },
];

const CALL_CARRIER_SUBMISSION_VARIABLES: MailVariable[] = [
  { key: "call_name", label: "Nom de l'appel", token: "{{ call_name }}", description: "Nom de l'appel à projets." },
  { key: "candidate_name", label: "Nom du candidat", token: "{{ candidate_name }}", description: "Nom complet du candidat." },
  { key: "candidate_email", label: "Email du candidat", token: "{{ candidate_email }}", description: "Adresse email du candidat." },
  { key: "project_name", label: "Nom du projet", token: "{{ project_name }}", description: "Nom du projet soumis." },
  { key: "project_type_name", label: "Type de projet", token: "{{ project_type_name }}", description: "Type du projet soumis." },
  { key: "project_url", label: "Lien vers le projet", token: "{{ project_url }}", description: "Lien vers le projet ou la candidature.", isLink: true },
];

const getVariablesForMailType = (mailType?: string): MailVariable[] => {
  if (
    mailType === "call_candidate_manual_add" ||
    mailType === "call_candidate_invitation" ||
    mailType === "call_candidate_access_recovery"
  ) {
    return CALL_CANDIDATE_ACCESS_VARIABLES;
  }

  if (mailType === "call_application_validated") {
    return CALL_APPLICATION_VALIDATED_VARIABLES;
  }

  if (mailType === "call_submission_confirmation") {
    return CALL_SUBMISSION_CONFIRMATION_VARIABLES;
  }

  if (mailType === "call_project_validated" || mailType === "call_project_rejected") {
    return CALL_PROJECT_DECISION_VARIABLES;
  }

  if (
    mailType === "call_submission_received" ||
    mailType === "call_submission_requires_approval" ||
    mailType === "call_submission_auto_validated"
  ) {
    return CALL_CARRIER_SUBMISSION_VARIABLES;
  }

  return CALL_MAIL_VARIABLES;
};

function ParametersEmailTemplates({ hideHeader = false }: { hideHeader?: boolean }) {
  const editorInstanceRef = React.useRef<TinyMceEditor | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const calls = useFetchDataV2<CallListItem[] | null>(`${API_URL}/call-for-project/all`, {
    staleTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
    gcTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
    browserCache: true,
    browserCacheKey: CALLS_EMAIL_BROWSER_CACHE_KEY,
    browserCacheTtlMs: EMAIL_TEMPLATES_CACHE_TTL_MS,
  });
  const globalTemplatesEndpoint = `${API_URL}/call-for-project/mail-templates/global`;
  const globalTemplatesQuery = useFetchDataV2<MailTemplateApiResponse | null>(
    globalTemplatesEndpoint,
    {
      staleTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
      gcTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
      browserCache: true,
      browserCacheKey: CALL_EMAIL_TEMPLATES_BROWSER_CACHE_KEY,
      browserCacheTtlMs: EMAIL_TEMPLATES_CACHE_TTL_MS,
    },
  );
  const { postData, loading: isSaving } = usePostData<any>();
  const { postData: postTestData, loading: isSendingTest } = usePostData<any>();
  const [editorState, setEditorState] = React.useState<EditorState | null>(null);
  const [testDialogOpen, setTestDialogOpen] = React.useState(false);
  const [testEmail, setTestEmail] = React.useState("");
  const [callTemplateCache, setCallTemplateCache] = React.useState<Record<number, MailTemplateApiResponse>>({});
  const [starterCache, setStarterCache] = React.useState<Record<string, StarterTemplatesResponse>>({});
  const [loadingStarterKey, setLoadingStarterKey] = React.useState<StarterKey | null>(null);
  const selectedCallId = Number(searchParams.get("callId") || 0) || null;
  const [openCallAccordion, setOpenCallAccordion] = React.useState<string>(selectedCallId ? `call-${selectedCallId}` : "");
  const globalSourceByKey = React.useMemo<Record<string, "verdeo_default" | "carrier_global" | "call">>(
    () =>
      Object.fromEntries(
        Object.entries(globalTemplatesQuery.data?.resolvedTemplates ?? {}).map(([key, template]) => [
          key,
          template.source,
        ]),
      ),
    [globalTemplatesQuery.data?.resolvedTemplates],
  );

  const callItems = React.useMemo<CallListItem[]>(
    () => {
      const items = Array.isArray(calls.data) ? calls.data : [];
      if (!selectedCallId) return items;
      return items.filter((call) => call.id === selectedCallId);
    },
    [calls.data, selectedCallId],
  );

  React.useEffect(() => {
    if (selectedCallId) {
      setOpenCallAccordion(`call-${selectedCallId}`);
    }
  }, [selectedCallId]);

  const loadCallTemplates = React.useCallback(
    async (callId: number) => {
      const cached = callTemplateCache[callId];
      if (cached) return cached;

      const response = await calls.fetchData(
        `${API_URL}/call-for-project/${callId}/mail-templates`,
        {
          queryKey: ["parameters-call-email-templates", callId],
          staleTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
          gcTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
        },
      );
      if (!response?.success) {
        toast({ title: "Impossible de charger les modèles de cet appel." });
        return null;
      }

      const data = response as MailTemplateApiResponse;
      setCallTemplateCache((prev) => ({ ...prev, [callId]: data }));
      return data;
    },
    [API_URL, callTemplateCache, calls],
  );

  React.useEffect(() => {
    if (!selectedCallId) return;
    void loadCallTemplates(selectedCallId);
  }, [loadCallTemplates, selectedCallId]);

  React.useEffect(() => {
    const body = document.body;
    if (!body) return;

    if (editorState) {
      body.classList.add("pointer-events-initial");
    } else {
      body.classList.remove("pointer-events-initial");
    }

    return () => {
      body.classList.remove("pointer-events-initial");
    };
  }, [editorState]);

  const handleOpenGlobalEditor = React.useCallback(
    (item: EmailModelItem) => {
      const data = globalTemplatesQuery.data;
      if (!data) {
        toast({ title: "Chargement des modèles en cours." });
        return;
      }

      const stored = data.storedTemplates[item.key];
      const resolved = data.resolvedTemplates[item.key];

      setEditorState({
        scope: "carrier_global",
        mailType: item.key,
        title: item.title,
        subject: stored?.subject ?? resolved?.subject ?? "",
        content: stored?.content ?? resolved?.content ?? "",
        useVerdeoDefault: !stored,
        hasStoredTemplate: !!stored,
        source: resolved?.source,
      });
    },
    [globalTemplatesQuery.data],
  );

  const handleOpenCallEditor = React.useCallback(
    async (call: CallListItem, item: EmailModelItem) => {
      const data = await loadCallTemplates(call.id);
      if (!data) return;

      const stored = data.storedTemplates[item.key];
      const resolved = data.resolvedTemplates[item.key];

      setEditorState({
        scope: "call",
        mailType: item.key,
        title: item.title,
        callId: call.id,
        callName: getCallDisplayName(call),
        subject: stored?.subject ?? resolved?.subject ?? "",
        content: stored?.content ?? resolved?.content ?? "",
        useVerdeoDefault: !stored,
        hasStoredTemplate: !!stored,
        source: resolved?.source,
      });
    },
    [loadCallTemplates],
  );

  const handleSaveEditor = React.useCallback(async () => {
    if (!editorState) return;

    const endpoint =
      editorState.scope === "carrier_global"
        ? globalTemplatesEndpoint
        : `${API_URL}/call-for-project/${editorState.callId}/mail-templates`;
    const editorSnapshot = editorState;
    const previousGlobalTemplates = globalTemplatesQuery.data ?? null;
    const previousCallTemplates = editorSnapshot.callId
      ? (callTemplateCache[editorSnapshot.callId] ?? null)
      : null;
    const applyTemplateUpdate = (
      current: MailTemplateApiResponse | null | undefined,
      serverResult?: any,
    ) => {
      if (!current) return current;

      const storedTemplates = {
        ...current.storedTemplates,
        ...(serverResult?.storedTemplates ?? {}),
      };

      if (editorSnapshot.useVerdeoDefault) {
        delete storedTemplates[editorSnapshot.mailType];
      } else {
        storedTemplates[editorSnapshot.mailType] = {
          subject: editorSnapshot.subject,
          content: editorSnapshot.content,
          useVerdeoDefault: false,
        };
      }

      const currentResolved = current.resolvedTemplates[editorSnapshot.mailType];
      const fallbackSource =
        editorSnapshot.scope === "call"
          ? current.globalTemplates?.[editorSnapshot.mailType]
            ? "carrier_global"
            : "verdeo_default"
          : "verdeo_default";
      const customSource =
        editorSnapshot.scope === "call" ? "call" : "carrier_global";
      const nextResolved =
        serverResult?.resolvedTemplate ??
        (currentResolved
          ? {
              ...currentResolved,
              subject: editorSnapshot.subject,
              content: editorSnapshot.content,
              useVerdeoDefault: editorSnapshot.useVerdeoDefault,
              source: editorSnapshot.useVerdeoDefault
                ? fallbackSource
                : customSource,
            }
          : currentResolved);

      return {
        ...current,
        storedTemplates,
        resolvedTemplates: nextResolved
          ? {
              ...current.resolvedTemplates,
              [editorSnapshot.mailType]: nextResolved,
            }
          : current.resolvedTemplates,
      };
    };

    if (editorSnapshot.scope === "carrier_global") {
      queryClient.setQueryData(
        ["fetchDataV2", globalTemplatesEndpoint],
        (current: MailTemplateApiResponse | null | undefined) =>
          applyTemplateUpdate(current ?? previousGlobalTemplates),
      );
    } else if (editorSnapshot.callId) {
      setCallTemplateCache((prev) => {
        const current = prev[editorSnapshot.callId!] ?? previousCallTemplates;
        if (!current) return prev;

        return {
          ...prev,
          [editorSnapshot.callId!]: applyTemplateUpdate(current) as MailTemplateApiResponse,
        };
      });
    }
    setEditorState(null);

    const result = await postData({
      url: endpoint,
      data: {
        mailType: editorSnapshot.mailType,
        subject: editorSnapshot.subject,
        content: editorSnapshot.content,
        useVerdeoDefault: editorSnapshot.useVerdeoDefault,
      },
    });

    if (!result?.success) {
      if (editorSnapshot.scope === "carrier_global") {
        queryClient.setQueryData(["fetchDataV2", globalTemplatesEndpoint], previousGlobalTemplates);
      } else if (editorSnapshot.callId) {
        setCallTemplateCache((prev) => {
          if (!previousCallTemplates) return prev;
          return {
            ...prev,
            [editorSnapshot.callId!]: previousCallTemplates,
          };
        });
      }
      setEditorState(editorSnapshot);
      toast({ title: "La sauvegarde du modèle a échoué." });
      return;
    }

    if (editorSnapshot.scope === "carrier_global") {
      queryClient.setQueryData(
        ["fetchDataV2", globalTemplatesEndpoint],
        (current: MailTemplateApiResponse | null | undefined) =>
          applyTemplateUpdate(current ?? previousGlobalTemplates, result),
      );
    } else if (editorSnapshot.callId) {
      setCallTemplateCache((prev) => {
        const current = prev[editorSnapshot.callId!] ?? previousCallTemplates;
        if (!current) return prev;

        return {
          ...prev,
          [editorSnapshot.callId!]: applyTemplateUpdate(current, result) as MailTemplateApiResponse,
        };
      });
    }

    toast({ title: "Modèle enregistré." });
  }, [
    API_URL,
    callTemplateCache,
    editorState,
    globalTemplatesEndpoint,
    globalTemplatesQuery.data,
    postData,
    queryClient,
  ]);

  const handleOpenTestDialog = React.useCallback(() => {
    const editor = editorInstanceRef.current;
    if (editor) {
      setEditorState((prev) => (prev ? { ...prev, content: editor.getContent() } : prev));
    }
    setTestDialogOpen(true);
  }, []);

  const getStarterCacheKey = React.useCallback(
    (state: EditorState) =>
      state.scope === "carrier_global"
        ? `global:${state.mailType}`
        : `call:${state.callId}:${state.mailType}`,
    [],
  );

  const loadStarterTemplates = React.useCallback(
    async (state: EditorState) => {
      const cacheKey = getStarterCacheKey(state);
      if (starterCache[cacheKey]) {
        return starterCache[cacheKey];
      }

      const endpoint =
        state.scope === "carrier_global"
          ? `${API_URL}/call-for-project/mail-templates/global/${state.mailType}/starters`
          : `${API_URL}/call-for-project/${state.callId}/mail-templates/${state.mailType}/starters`;

      const response = await calls.fetchData(endpoint, {
        queryKey: ["parameters-email-template-starters", cacheKey],
        staleTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
        gcTime: EMAIL_TEMPLATES_CACHE_TTL_MS,
      });

      if (!response?.success) {
        toast({ title: "Impossible de charger les modèles de départ." });
        return null;
      }

      const data = response as StarterTemplatesResponse;
      setStarterCache((prev) => ({ ...prev, [cacheKey]: data }));
      return data;
    },
    [API_URL, calls, getStarterCacheKey, starterCache],
  );

  const handleSendTestEmail = React.useCallback(async () => {
    if (!editorState) return;

    const editor = editorInstanceRef.current;
    let subject = editorState.subject;
    let content = editor && !editorState.useVerdeoDefault ? editor.getContent() : editorState.content;
    const testEmailSnapshot = testEmail;
    const shouldRenderNative =
      editorState.useVerdeoDefault &&
      (editorState.scope === "carrier_global" || editorState.source === "verdeo_default");

    if (shouldRenderNative) {
      setTestDialogOpen(false);
      setTestEmail("");
      toast({ title: "Mail natif de test envoyé." });

      const result = await postTestData({
        url: `${API_URL}/mail-templates/test-send`,
        data: {
          email: testEmailSnapshot,
          mailType: editorState.mailType,
          scope: "call",
          callId: editorState.callId,
          native: true,
        },
      });

      if (!result?.success) {
        setTestEmail(testEmailSnapshot);
        setTestDialogOpen(true);
        toast({ title: result?.message || "L'envoi du mail natif de test a échoué." });
        return;
      }

      return;
    }

    if (!content.trim()) {
      setLoadingStarterKey("native");
      const data = await loadStarterTemplates(editorState);
      setLoadingStarterKey(null);
      const nativeTemplate = data?.templates?.native;

      if (nativeTemplate) {
        subject = nativeTemplate.subject || subject;
        content = nativeTemplate.content || content;
      }
    }

    if (!content.trim()) {
      toast({ title: "Impossible de charger le contenu natif du mail." });
      return;
    }

    setTestDialogOpen(false);
    setTestEmail("");
    toast({ title: "Mail de test envoyé." });

    const result = await postTestData({
      url: `${API_URL}/mail-templates/test-send`,
      data: {
        email: testEmailSnapshot,
        mailType: editorState.mailType,
        subject,
        content,
      },
    });

    if (!result?.success) {
      setTestEmail(testEmailSnapshot);
      setTestDialogOpen(true);
      toast({ title: result?.message || "L'envoi du mail de test a échoué." });
      return;
    }
  }, [API_URL, editorState, loadStarterTemplates, postTestData, testEmail]);

  const handleToggleUseDefault = React.useCallback(
    async (checked: boolean) => {
      const current = editorState;
      if (!current) return;

      if (checked) {
        setEditorState((prev) => (prev ? { ...prev, useVerdeoDefault: true } : prev));
        return;
      }

      setEditorState((prev) =>
        prev
          ? {
              ...prev,
              useVerdeoDefault: false,
            }
          : prev,
      );

      if (!current.hasStoredTemplate) {
        setLoadingStarterKey("native");
        const data = await loadStarterTemplates(current);
        setLoadingStarterKey(null);
        const template = data?.templates?.native;
        if (!template) {
          toast({ title: "Modèle natif indisponible." });
          return;
        }

        setEditorState((prev) =>
          prev
            ? {
                ...prev,
                subject: template.subject,
                content: template.content,
                useVerdeoDefault: false,
              }
            : prev,
        );
      }
    },
    [editorState, loadStarterTemplates],
  );

  const handleTinyMceOutsideInteraction = React.useCallback((event: Event) => {
    const target = event.target as HTMLElement | null;
    const tinyPopupOpen = !!document.querySelector(
      ".tox-dialog, .tox-dialog-wrap, .tox-tinymce-aux, .tox-silver-sink, .tox-menu, .tox-collection, .tox-pop",
    );

    if (tinyPopupOpen) {
      event.preventDefault();
      return;
    }

    if (
      target?.closest(
        ".tox-tinymce-aux, .tox-silver-sink, .tox-dialog, .tox-dialog-wrap, .tox-menu, .tox-collection, .tox-pop",
      )
    ) {
      event.preventDefault();
    }

    if (target?.closest("[data-email-test-dialog]")) {
      event.preventDefault();
    }
  }, []);
  const handleInsertVariable = React.useCallback((token: string) => {
    if (!editorState || editorState.useVerdeoDefault) return;

    const editor = editorInstanceRef.current;
    if (editor) {
      editor.insertContent(token);
      setEditorState((prev) => (prev ? { ...prev, content: editor.getContent() } : prev));
      return;
    }

    setEditorState((prev) =>
      prev
        ? { ...prev, content: prev.content ? `${prev.content} ${token}` : token }
        : prev,
    );
  }, [editorState]);

  const handleInsertLinkVariable = React.useCallback((token: string) => {
    if (!editorState || editorState.useVerdeoDefault) return;

    const html = `<a href="${token}">${token}</a>`;
    const editor = editorInstanceRef.current;
    if (editor) {
      editor.insertContent(html);
      setEditorState((prev) => (prev ? { ...prev, content: editor.getContent() } : prev));
      return;
    }

    setEditorState((prev) =>
      prev
        ? { ...prev, content: prev.content ? `${prev.content} ${html}` : html }
        : prev,
    );
  }, [editorState]);

  const getCallSourceByKey = React.useCallback(
    (callId: number): Record<string, "verdeo_default" | "carrier_global" | "call"> =>
      Object.fromEntries(
        Object.entries(callTemplateCache[callId]?.resolvedTemplates ?? {}).map(([key, template]) => [
          key,
          template.source,
        ]),
      ),
    [callTemplateCache],
  );

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div className="rounded-3xl border border-[#dfe9d8] bg-[linear-gradient(135deg,#ffffff_0%,#f4f8ef_100%)] p-5 md:p-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#edf6e7] px-3 py-1 text-xs font-medium text-[#486a2f]">
                <Mail className="size-3.5" />
                Modèles d’email
              </div>
              <h1 className="text-2xl font-semibold tracking-tight text-[#18351b]">
                Paramétrage des emails
              </h1>
              <p className="max-w-3xl text-sm text-[#5f6d5c]">
                Définissez vos modèles globaux, puis les modèles dédiés à chaque appel à projet si
                nécessaire.
              </p>
            </div>
            <Link
              to="/dashboard/parameters?params=2"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#486a2f] hover:text-[#274e1f]"
            >
              Voir les formulaires
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      )}

      {!selectedCallId && (
        <SectionCard
          title="Emails génériques – Appels à projets"
          description="Ces modèles d’emails sont utilisés par défaut pour tous vos appels à projets. Toutefois, si un appel à projets dispose de ses propres modèles personnalisés, ceux-ci remplacent les modèles génériques."
        >
          <EmailGroupAccordion
            groups={GLOBAL_EMAIL_GROUPS}
            valuePrefix="global"
            onConfigure={handleOpenGlobalEditor}
            sourceByKey={globalSourceByKey}
            sourceLabels={{ inherited: "Verdeo", customized: "Personnalisé" }}
            customizedSources={["carrier_global"]}
          />
        </SectionCard>
      )}

      <SectionCard
        title={
          selectedCallId
            ? "Emails de l’appel à projet"
            : "Emails spécifiques – Appels à projets"
        }
        description={
          selectedCallId
            ? "Vous paramétrez ici uniquement les emails spécifiques à cet appel à projet."
            : "Chaque appel à projets peut disposer de ses propres modèles d’emails, qui remplacent alors les modèles génériques, pour les candidats comme pour les porteurs de projet."
        }
      >
        {calls.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="rounded-2xl border border-[#e6eee0] bg-[#fbfdf9] p-4">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="mt-3 h-4 w-72" />
              </div>
            ))}
          </div>
        ) : (
          <Accordion
            type="single"
            collapsible={!selectedCallId}
            value={openCallAccordion}
            onValueChange={(value) => {
              setOpenCallAccordion(value);
              const match = value.match(/^call-(\d+)$/);
              if (match?.[1]) {
                void loadCallTemplates(Number(match[1]));
              }
            }}
            className="space-y-3"
          >
            {callItems.map((call) => (
              <AccordionItem
                key={call.id}
                value={`call-${call.id}`}
                className="overflow-hidden rounded-2xl border border-[#e1eadb] bg-[#fbfdf9] px-4"
              >
                <AccordionTrigger className="py-4 hover:no-underline">
                  <div className="flex w-full flex-col gap-3 pr-4 text-left md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-semibold text-[#18351b]">
                          {getCallDisplayName(call)}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#6c796a]">
                        <span className="inline-flex items-center gap-1.5">
                          <FileText className="size-3.5" />
                          Type de projet : {call.projectType?.name || "Non défini"}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <ShieldCheck className="size-3.5" />
                          Date limite : {formatDeadline(call.endAt ?? call.dateFin)}
                        </span>
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <EmailGroupAccordion
                    valuePrefix={`call-${call.id}`}
                    groups={CALL_SPECIFIC_GROUPS.map((group) => ({
                      ...group,
                      items: group.items.map((item) => ({
                        ...item,
                      })),
                    }))}
                    sourceByKey={getCallSourceByKey(call.id)}
                    sourceLabels={{ inherited: "Général", customized: "Personnalisé" }}
                    customizedSources={["call"]}
                    onConfigure={(item) => handleOpenCallEditor(call, item)}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </SectionCard>

      <Dialog modal={false} open={!!editorState} onOpenChange={(open) => !open && setEditorState(null)}>
        <DialogContent
          id="email-template-dialog-content"
          className="max-w-5xl"
          staticOverlay
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={handleTinyMceOutsideInteraction}
          onPointerDownOutside={handleTinyMceOutsideInteraction}
          onFocusOutside={handleTinyMceOutsideInteraction}
        >
          <DialogHeader>
            <DialogTitle>{editorState?.title}</DialogTitle>
            <DialogDescription>
              {editorState?.scope === "carrier_global"
                ? "Ce modèle s’applique à tous vos appels à projets, sauf si un appel possède sa propre surcharge."
                : `Ce modèle surcharge les emails globaux pour l’appel ${editorState?.callName ?? ""}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="flex items-center justify-between rounded-2xl border border-[#dfe9d8] bg-[#f8fbf5] px-4 py-3">
              <div className="space-y-1">
                <p className="text-sm font-medium text-[#18351b]">
                  {editorState?.scope === "carrier_global"
                    ? "Utiliser les emails de Verdeo"
                    : "Utiliser les modèles globaux"}
                </p>
                <p className="text-xs text-[#6c796a]">
                  {editorState?.scope === "carrier_global"
                    ? "Si activé, ce modèle hérite du mail par défaut déjà en place."
                    : "Si activé, ce modèle hérite du modèle global de vos appels à projets."}
                </p>
              </div>
              <Switch
                checked={editorState?.useVerdeoDefault ?? true}
                onCheckedChange={handleToggleUseDefault}
              />
            </div>

            {editorState?.useVerdeoDefault ? (
              <div className="rounded-2xl border border-[#dfe9d8] bg-[#fbfdf9] px-4 py-5 text-sm text-[#5f6d5c]">
                <p>
                  {editorState?.scope === "carrier_global"
                    ? "Les contenus par défaut de Verdeo seront utilisés pour cet email."
                    : "Le modèle global des appels à projets sera utilisé pour cet email."}
                </p>
                <Button
                  type="button"
                  className="mt-4 gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                  disabled={!editorState || isSendingTest}
                  onClick={handleOpenTestDialog}
                >
                  <FlaskConical className="size-4" />
                  Tester le modèle
                </Button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
                  <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="email-template-subject">Objet</Label>
                    <Input
                      id="email-template-subject"
                      value={editorState?.subject ?? ""}
                      onChange={(event) =>
                        setEditorState((prev) =>
                          prev ? { ...prev, subject: event.target.value } : prev,
                        )
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email-template-content">Contenu</Label>
                    <EditorIntro
                      value={editorState?.content ?? ""}
                      height={360}
                      uiContainerSelector="#email-template-dialog-content"
                      onReady={(editor) => {
                        editorInstanceRef.current = editor;
                      }}
                      onChange={(value) =>
                        setEditorState((prev) => (prev ? { ...prev, content: value } : prev))
                      }
                    />
                  </div>
                  </div>

                  <aside className="rounded-2xl border border-[#dfe9d8] bg-[#fbfdf9] p-4">
                  <Button
                    type="button"
                    className="mb-4 w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                    disabled={!editorState || editorState.useVerdeoDefault || !!loadingStarterKey}
                    onClick={handleOpenTestDialog}
                  >
                    <FlaskConical className="size-4" />
                    Tester le modèle
                  </Button>
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-[#18351b]">Variables disponibles</h3>
                    <p className="text-xs leading-5 text-[#6c796a]">
                      Clique sur une variable pour l’insérer dans le contenu du mail.
                    </p>
                  </div>

                  <div className="mt-4 max-h-[430px] space-y-2 overflow-y-auto pr-1">
                    {getVariablesForMailType(editorState?.mailType).map((variable) => (
                      <div
                        key={variable.key}
                        className="rounded-xl border border-[#dfe9d8] bg-white px-3 py-2"
                      >
                        <p className="text-sm font-medium text-[#18351b]">{variable.label}</p>
                        <p className="mt-1 text-xs leading-5 text-[#6c796a]">{variable.description}</p>
                        <code className="mt-2 block rounded-md bg-[#eef4e8] px-2 py-1 text-[11px] text-[#36552a]">
                          {variable.token}
                        </code>
                        <div className="mt-3 flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 border-[#dfe9d8] text-[#2f4b2d] hover:border-[#bfd2b2] hover:bg-[#f4f8ef]"
                            onClick={() => handleInsertVariable(variable.token)}
                          >
                            Insérer
                          </Button>
                          {variable.isLink && (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-8 border-[#dfe9d8] text-[#2f4b2d] hover:border-[#bfd2b2] hover:bg-[#f4f8ef]"
                              onClick={() => handleInsertLinkVariable(variable.token)}
                            >
                              Insérer en lien
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  </aside>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEditorState(null)}>
              Annuler
            </Button>
            <Button
              type="button"
              className="bg-[#486a2f] text-white hover:bg-[#3d5a27]"
              disabled={isSaving || !!loadingStarterKey}
              onClick={handleSaveEditor}
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen} modal={false}>
        <DialogContent
          className="z-[10001] max-w-md"
          staticOverlay
          overlayClassName="z-[10000]"
          data-email-test-dialog
        >
          <DialogHeader>
            <DialogTitle>Tester le modèle</DialogTitle>
            <DialogDescription>
              Indiquez l’adresse email qui recevra ce modèle avec des données fictives.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="call-email-template-test-recipient">Email destinataire</Label>
            <Input
              id="call-email-template-test-recipient"
              type="email"
              value={testEmail}
              placeholder="exemple@domaine.fr"
              onChange={(event) => setTestEmail(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTestDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              className="bg-[#486a2f] text-white hover:bg-[#3d5a27]"
              disabled={isSendingTest || !testEmail.trim()}
              onClick={handleSendTestEmail}
            >
              {isSendingTest ? "Envoi..." : "Envoyer le test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ParametersEmailTemplates;
